import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Chip,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  EmojiEvents as EmojiEventsIcon,
} from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { format, endOfMonth, startOfMonth, startOfYear, subDays } from "date-fns";
import useIsMobileList from "../../hooks/useIsMobileList";
import MobileListCard from "../../components/MobileListCard";
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import useAuth from "../../hooks/useAuth";
import {
  createMilking,
  createMilkPrice,
  createMilkSale,
  exportMilkPricesCsv,
  exportMilkSalesCsv,
  getMilkDashboardSummary,
  getMilkPrices,
  getMilkPricesPaginated,
  getMilkSales,
  getMilkSalesPaginated,
  getMilkTrends,
  updateMilkPrice,
  updateMilkSale,
} from "../../api/milking";
import { searchAnimalsQuick } from "../../api/animal";

function buildRangeParams(range, customStartDate, customEndDate) {
  const now = new Date();
  if (range === "thisMonth") {
    return {
      start_date: format(startOfMonth(now), "yyyy-MM-dd"),
      end_date: format(endOfMonth(now), "yyyy-MM-dd"),
    };
  }
  if (range === "thisYear") {
    return {
      start_date: format(startOfYear(now), "yyyy-MM-dd"),
      end_date: format(now, "yyyy-MM-dd"),
    };
  }
  if (range === "custom") {
    return {
      start_date: customStartDate,
      end_date: customEndDate,
    };
  }
  return {
    start_date: format(subDays(now, 29), "yyyy-MM-dd"),
    end_date: format(now, "yyyy-MM-dd"),
  };
}

function getBrushStartIndex(length, visibleCount = 14) {
  if (!length || length <= visibleCount) return 0;
  return Math.max(length - visibleCount, 0);
}

function isMilkingEligibleAnimal(animal) {
  if (!animal) return false;
  const gender = String(animal.gender || "").toLowerCase().trim();
  const category = String(animal.category || "").toLowerCase().trim();
  const normalizedGender = gender.replace(/[^a-z0-9]+/g, " ");
  const normalizedCategory = category.replace(/[^a-z0-9]+/g, " ");
  const hasToken = (value, token) =>
    value === token || value.startsWith(`${token} `) || value.endsWith(` ${token}`) || value.includes(` ${token} `);
  const negativeMarkers = ["male", "bull", "steer", "ox"];
  const positiveGenderMarkers = ["female", "cow", "heifer"];
  const positiveCategoryMarkers = ["cow", "heifer", "dairy", "milking"];
  if (
    positiveGenderMarkers.some((marker) => hasToken(normalizedGender, marker)) ||
    positiveCategoryMarkers.some((marker) => hasToken(normalizedCategory, marker))
  ) {
    return true;
  }
  if (
    negativeMarkers.some(
      (marker) => hasToken(normalizedGender, marker) || hasToken(normalizedCategory, marker)
    )
  ) {
    return false;
  }
  return false;
}

function StatCard({ label, value, helper, accent, background }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        minHeight: 132,
        height: "100%",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        background: background || "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: "0 auto 0 0",
          width: 5,
          backgroundColor: accent || "#1565c0",
        },
      }}
    >
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.9 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.75 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {helper}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function MilkDashboardPanel() {
  const { user } = useAuth();
  const isMobile = useIsMobileList("md");
  const [range, setRange] = useState("last30");
  const [customStartDate, setCustomStartDate] = useState(format(subDays(new Date(), 29), "yyyy-MM-dd"));
  const [customEndDate, setCustomEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [prices, setPrices] = useState([]);
  const [sales, setSales] = useState([]);
  const [priceTotal, setPriceTotal] = useState(0);
  const [salesTotal, setSalesTotal] = useState(0);
  const [pricePage, setPricePage] = useState(0);
  const [pricePageSize, setPricePageSize] = useState(5);
  const [salesPage, setSalesPage] = useState(0);
  const [salesPageSize, setSalesPageSize] = useState(5);
  const [allPrices, setAllPrices] = useState([]);
  const [allSales, setAllSales] = useState([]);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [editingSaleId, setEditingSaleId] = useState(null);
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  const [quickAnimalInput, setQuickAnimalInput] = useState("");
  const [quickAnimalOptions, setQuickAnimalOptions] = useState([]);
  const [quickCaptureLoading, setQuickCaptureLoading] = useState(false);
  const [quickCaptureSaving, setQuickCaptureSaving] = useState(false);
  const [quickCaptureError, setQuickCaptureError] = useState("");
  const [quickCaptureSuccess, setQuickCaptureSuccess] = useState("");
  const [quickAnimal, setQuickAnimal] = useState(null);
  const [quickDate, setQuickDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [quickMorning, setQuickMorning] = useState("");
  const [quickEvening, setQuickEvening] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [priceForm, setPriceForm] = useState({
    effective_date: format(new Date(), "yyyy-MM-dd"),
    price_per_liter: "",
    notes: "",
  });
  const [saleForm, setSaleForm] = useState({
    sale_date: format(new Date(), "yyyy-MM-dd"),
    liters_sold: "",
    unit_price: "",
    buyer: "",
    payment_status: "Paid",
    payment_method: "",
    notes: "",
  });

  const canManageMilk =
    user?.roles?.includes("super_admin") ||
    user?.roles?.includes("TenantAdmin") ||
    user?.permissions?.includes("manage_milk");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = buildRangeParams(range, customStartDate, customEndDate);
      const [summaryData, trendsData, priceData, salesData, allPriceData, allSalesData] = await Promise.all([
        getMilkDashboardSummary({ ...params, top_limit: 3 }),
        getMilkTrends({ ...params, aggregation: "daily" }),
        getMilkPricesPaginated({ page: pricePage + 1, page_size: pricePageSize }),
        getMilkSalesPaginated({
          ...params,
          page: salesPage + 1,
          page_size: salesPageSize,
        }),
        getMilkPrices(),
        getMilkSales(params),
      ]);
      setSummary(summaryData);
      setTrends(trendsData?.points || []);
      setPrices(priceData?.items || []);
      setPriceTotal(priceData?.total || 0);
      setSales(salesData?.items || []);
      setSalesTotal(salesData?.total || 0);
      setAllPrices(allPriceData || []);
      setAllSales(allSalesData || []);
    } catch (err) {
      setError(err?.detail || err?.message || "Failed to load milk dashboard");
    } finally {
      setLoading(false);
    }
  }, [range, customStartDate, customEndDate, pricePage, pricePageSize, salesPage, salesPageSize]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!quickCaptureOpen) return;
    const q = String(quickAnimalInput || "").trim();
    if (q.length < 2) {
      setQuickAnimalOptions([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setQuickCaptureLoading(true);
        const rows = await searchAnimalsQuick(q);
        if (cancelled) return;
        const eligible = (rows || []).filter(isMilkingEligibleAnimal);
        setQuickAnimalOptions(eligible);
      } finally {
        if (!cancelled) setQuickCaptureLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [quickCaptureOpen, quickAnimalInput]);

  const onPriceSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...priceForm,
      price_per_liter: Number(priceForm.price_per_liter),
    };
    if (editingPriceId) {
      await updateMilkPrice(editingPriceId, payload);
    } else {
      await createMilkPrice(payload);
    }
    setPriceForm({
      effective_date: format(new Date(), "yyyy-MM-dd"),
      price_per_liter: "",
      notes: "",
    });
    setEditingPriceId(null);
    await load();
  };

  const onSaleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...saleForm,
      liters_sold: Number(saleForm.liters_sold),
      unit_price: Number(saleForm.unit_price),
    };
    if (editingSaleId) {
      await updateMilkSale(editingSaleId, payload);
    } else {
      await createMilkSale(payload);
    }
    setSaleForm((prev) => ({
      sale_date: format(new Date(), "yyyy-MM-dd"),
      liters_sold: "",
      unit_price: "",
      buyer: "",
      payment_status: "Paid",
      payment_method: "",
      notes: "",
    }));
    setEditingSaleId(null);
    await load();
  };
  const resetQuickCapture = useCallback(() => {
    setQuickCaptureError("");
    setQuickCaptureSuccess("");
    setQuickAnimalInput("");
    setQuickAnimalOptions([]);
    setQuickAnimal(null);
    setQuickDate(format(new Date(), "yyyy-MM-dd"));
    setQuickMorning("");
    setQuickEvening("");
  }, []);
  const openQuickCapture = useCallback(() => {
    resetQuickCapture();
    setQuickCaptureOpen(true);
  }, [resetQuickCapture]);
  const closeQuickCapture = useCallback(() => {
    if (quickCaptureSaving) return;
    setQuickCaptureOpen(false);
  }, [quickCaptureSaving]);
  const onQuickCaptureSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setQuickCaptureError("");
      setQuickCaptureSuccess("");
      if (!quickAnimal?.id) {
        setQuickCaptureError("Select a cow before saving.");
        return;
      }
      try {
        setQuickCaptureSaving(true);
        await createMilking({
          animal_id: quickAnimal.id,
          date: quickDate,
          morning_production: Number(quickMorning || 0),
          evening_production: Number(quickEvening || 0),
        });
        setQuickCaptureSuccess("Milking record saved.");
        await load();
        resetQuickCapture();
      } catch (err) {
        setQuickCaptureError(err?.detail || err?.message || "Failed to save milking record");
      } finally {
        setQuickCaptureSaving(false);
      }
    },
    [load, quickAnimal, quickDate, quickMorning, quickEvening, resetQuickCapture]
  );

  const currentPrice = prices[0] || null;
  const topCows = summary?.top_cows || [];
  const statSummary = summary?.summary || null;
  const formatNumber = useCallback(
    (value, digits = 2) =>
      new Intl.NumberFormat("en-UG", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(Number(value || 0)),
    []
  );
  const formatCurrency = useCallback(
    (value) => `UGX ${formatNumber(value, 2)}`,
    [formatNumber]
  );
  const formatCurrencyThousands = useCallback(
    (value) => `UGX ${formatNumber(Math.round(Number(value || 0) / 1000), 0)}.000`,
    [formatNumber]
  );
  const chartData = useMemo(
    () =>
      trends.map((point) => ({
        period: point.period,
        liters: point.total_production,
        estimated: point.estimated_income,
        actual: point.actual_income,
      })),
    [trends]
  );
  const salesVsPriceData = useMemo(() => {
    if (allSales.length === 0) return [];
    const sortedPrices = [...allPrices]
      .filter((row) => row?.effective_date)
      .sort((a, b) => a.effective_date.localeCompare(b.effective_date));
    const salesByDate = allSales.reduce((acc, sale) => {
      const key = sale.sale_date;
      if (!acc[key]) {
        acc[key] = { sale_date: key, liters_sold: 0, sales_amount: 0 };
      }
      acc[key].liters_sold += Number(sale.liters_sold || 0);
      acc[key].sales_amount += Number(sale.total_amount || 0);
      return acc;
    }, {});
    return Object.values(salesByDate)
      .sort((a, b) => a.sale_date.localeCompare(b.sale_date))
      .map((row) => {
        const effectivePrice = [...sortedPrices]
          .reverse()
          .find((price) => price.effective_date <= row.sale_date);
        return {
          period: row.sale_date,
          litersSold: row.liters_sold,
          salesAmount: row.sales_amount,
          marketPrice: Number(effectivePrice?.price_per_liter || 0),
        };
      });
  }, [allPrices, allSales]);
  const startPriceEdit = useCallback((row) => {
    setEditingPriceId(row.id);
    setPriceForm({
      effective_date: row.effective_date || format(new Date(), "yyyy-MM-dd"),
      price_per_liter: String(row.price_per_liter ?? ""),
      notes: row.notes || "",
    });
  }, []);
  const cancelPriceEdit = useCallback(() => {
    setEditingPriceId(null);
    setPriceForm({
      effective_date: format(new Date(), "yyyy-MM-dd"),
      price_per_liter: "",
      notes: "",
    });
  }, []);
  const startSaleEdit = useCallback((row) => {
    setEditingSaleId(row.id);
    setSaleForm({
      sale_date: row.sale_date || format(new Date(), "yyyy-MM-dd"),
      liters_sold: String(row.liters_sold ?? ""),
      unit_price: String(row.unit_price ?? ""),
      buyer: row.buyer || "",
      payment_status: row.payment_status || "Paid",
      payment_method: row.payment_method || "",
      notes: row.notes || "",
    });
  }, []);
  const cancelSaleEdit = useCallback(() => {
    setEditingSaleId(null);
    setSaleForm({
      sale_date: format(new Date(), "yyyy-MM-dd"),
      liters_sold: "",
      unit_price: "",
      buyer: "",
      payment_status: "Paid",
      payment_method: "",
      notes: "",
    });
  }, []);
  const priceColumns = useMemo(
    () => [
      { field: "effective_date", headerName: "Date", minWidth: 130, flex: 1 },
      {
        field: "price_per_liter",
        headerName: "Price / L",
        minWidth: 140,
        flex: 1,
        valueFormatter: (value) => formatCurrency(value),
      },
      { field: "notes", headerName: "Notes", minWidth: 180, flex: 1.5, valueGetter: (_, row) => row?.notes || "—" },
      ...(canManageMilk
        ? [
            {
              field: "actions",
              headerName: "Actions",
              minWidth: 120,
              sortable: false,
              filterable: false,
              renderCell: ({ row }) => (
                <Button size="small" startIcon={<EditIcon />} onClick={() => startPriceEdit(row)}>
                  Edit
                </Button>
              ),
            },
          ]
        : []),
    ],
    [canManageMilk, formatCurrency, startPriceEdit]
  );
  const saleColumns = useMemo(
    () => [
      { field: "sale_date", headerName: "Date", minWidth: 120, flex: 1 },
      { field: "buyer", headerName: "Buyer", minWidth: 140, flex: 1.2, valueGetter: (_, row) => row?.buyer || "—" },
      {
        field: "liters_sold",
        headerName: "Liters",
        minWidth: 120,
        flex: 1,
        valueFormatter: (value) => `${formatNumber(value, 2)} L`,
      },
      {
        field: "total_amount",
        headerName: "Amount",
        minWidth: 150,
        flex: 1.2,
        valueFormatter: (value) => formatCurrency(value),
      },
      {
        field: "payment_status",
        headerName: "Status",
        minWidth: 140,
        flex: 1,
      },
      ...(canManageMilk
        ? [
            {
              field: "actions",
              headerName: "Actions",
              minWidth: 120,
              sortable: false,
              filterable: false,
              renderCell: ({ row }) => (
                <Button size="small" startIcon={<EditIcon />} onClick={() => startSaleEdit(row)}>
                  Edit
                </Button>
              ),
            },
          ]
        : []),
    ],
    [canManageMilk, formatCurrency, formatNumber, startSaleEdit]
  );
  const statCards = [
    {
      label: "Total milk",
      value: `${formatNumber(statSummary?.total_production || 0, 2)} L`,
      accent: "#2e7d32",
      background: "linear-gradient(180deg, #FFFFFF 0%, #F1F8F4 100%)",
    },
    {
      label: "Estimated income",
      value: formatCurrency(statSummary?.estimated_income || 0),
      accent: "#1565c0",
      background: "linear-gradient(180deg, #FFFFFF 0%, #EFF6FF 100%)",
    },
    {
      label: "Actual milk income",
      value: formatCurrency(statSummary?.actual_income || 0),
      accent: "#ef6c00",
      background: "linear-gradient(180deg, #FFFFFF 0%, #FFF4E5 100%)",
    },
    {
      label: "Current milk price",
      value: currentPrice
        ? `${formatCurrency(currentPrice.price_per_liter || 0)} / L`
        : "Not set",
      helper: currentPrice
        ? `Effective ${currentPrice.effective_date}`
        : "Add a daily market price below",
      accent: "#6d4c41",
      background: "linear-gradient(180deg, #FFFFFF 0%, #F8F4F1 100%)",
    },
  ];
  const medalConfig = [
    { label: "Gold", color: "#f59e0b", bg: "#fff7e6", border: "#f6c453" },
    { label: "Silver", color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
    { label: "Bronze", color: "#b45309", bg: "#fff7ed", border: "#fdba74" },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Milk dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Production, estimated value, actual sales, and daily pricing in one place.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
            {canManageMilk ? (
              <Button variant="contained" onClick={openQuickCapture}>
                Quick record milk
              </Button>
            ) : null}
            <TextField
              select
              size="small"
              label="Period"
              value={range}
              onChange={(e) => {
                setRange(e.target.value);
                setSalesPage(0);
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="last30">Last 30 days</MenuItem>
              <MenuItem value="thisMonth">This month</MenuItem>
              <MenuItem value="thisYear">This year</MenuItem>
              <MenuItem value="custom">Custom range</MenuItem>
            </TextField>
            {range === "custom" ? (
              <>
                <TextField
                  size="small"
                  label="Start date"
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="End date"
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </>
            ) : null}
          </Stack>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              xl: "repeat(4, minmax(0, 1fr))",
            },
            mb: 2.5,
          }}
        >
          {statCards.map((card) => (
            <StatCard key={card.label} {...card} />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 2fr) minmax(300px, 1fr)",
            },
            alignItems: "start",
            mb: 2,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              minHeight: 360,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Production and income trend
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Income axis is shown in thousands of UGX for a cleaner read.
            </Typography>
            {loading ? (
              <Typography color="text.secondary">Loading trend data...</Typography>
            ) : chartData.length === 0 ? (
              <Alert severity="info">No milk data yet for the selected period.</Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis yAxisId="liters" orientation="left" tickFormatter={(value) => formatNumber(value, 0)} />
                  <YAxis yAxisId="income" orientation="right" tickFormatter={(value) => formatCurrencyThousands(value)} width={88} />
                  <Tooltip
                    formatter={(value, name) =>
                      name === "Liters produced"
                        ? [`${formatNumber(value, 2)} L`, name]
                        : [formatCurrency(value), name]
                    }
                  />
                  <Legend />
                  <Line yAxisId="liters" type="monotone" dataKey="liters" name="Liters produced" stroke="#2e7d32" strokeWidth={2} dot={false} />
                  <Line yAxisId="income" type="monotone" dataKey="estimated" name="Estimated income" stroke="#1565c0" strokeWidth={2} dot={false} />
                  <Line yAxisId="income" type="monotone" dataKey="actual" name="Actual income" stroke="#ef6c00" strokeWidth={2} dot={false} />
                  <Brush
                    dataKey="period"
                    height={26}
                    stroke="#94a3b8"
                    travellerWidth={10}
                    startIndex={getBrushStartIndex(chartData.length)}
                    endIndex={Math.max(chartData.length - 1, 0)}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              minHeight: 360,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Top producing cows
            </Typography>
            {topCows.length === 0 ? (
              <Typography color="text.secondary">No production records yet.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {topCows.map((cow, index) => {
                  const medal = medalConfig[index] || medalConfig[2];
                  return (
                    <Paper
                      key={cow.animal_id}
                      variant="outlined"
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        borderColor: medal.border,
                        background: `linear-gradient(180deg, ${medal.bg} 0%, #ffffff 100%)`,
                        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.05)",
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start" justifyContent="space-between">
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: "50%",
                              display: "grid",
                              placeItems: "center",
                              backgroundColor: medal.color,
                              color: "#fff",
                              boxShadow: `0 8px 20px ${medal.color}55`,
                            }}
                          >
                            <EmojiEventsIcon fontSize="small" />
                          </Box>
                          <Box>
                            <Typography sx={{ fontWeight: 800 }}>
                              {cow.alias || cow.tag_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {cow.tag_number}
                            </Typography>
                          </Box>
                        </Stack>
                        <Chip
                          size="small"
                          label={`#${index + 1} ${medal.label}`}
                          sx={{
                            fontWeight: 700,
                            color: medal.color,
                            backgroundColor: "#fff",
                            border: "1px solid",
                            borderColor: medal.border,
                          }}
                        />
                      </Stack>
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.25,
                          borderRadius: 2,
                          backgroundColor: "rgba(255,255,255,0.75)",
                          border: "1px solid",
                          borderColor: "rgba(148, 163, 184, 0.18)",
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Total milk produced
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: medal.color }}>
                          {formatNumber(cow.total_production || 0, 2)} L
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Milk price vs sales trend
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Compare market price per liter against sold liters and total sales value across the selected period.
          </Typography>
          {loading ? (
            <Typography color="text.secondary">Loading analysis...</Typography>
          ) : salesVsPriceData.length === 0 ? (
            <Alert severity="info">No milk sales found for the selected period.</Alert>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={salesVsPriceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis
                  yAxisId="price"
                  orientation="left"
                  tickFormatter={(value) => formatCurrency(value)}
                  width={90}
                />
                <YAxis
                  yAxisId="sales"
                  orientation="right"
                  tickFormatter={(value) => formatCurrencyThousands(value)}
                  width={88}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === "Market price") return [formatCurrency(value), name];
                    if (name === "Liters sold") return [`${formatNumber(value, 2)} L`, name];
                    return [formatCurrency(value), name];
                  }}
                />
                <Legend />
                <Line yAxisId="price" type="monotone" dataKey="marketPrice" name="Market price" stroke="#7c3aed" strokeWidth={2} dot={false} />
                <Line yAxisId="sales" type="monotone" dataKey="litersSold" name="Liters sold" stroke="#2e7d32" strokeWidth={2} dot={false} />
                <Line yAxisId="sales" type="monotone" dataKey="salesAmount" name="Sales value" stroke="#ef6c00" strokeWidth={2} dot={false} />
                <Brush
                  dataKey="period"
                  height={26}
                  stroke="#94a3b8"
                  travellerWidth={10}
                  startIndex={getBrushStartIndex(salesVsPriceData.length)}
                  endIndex={Math.max(salesVsPriceData.length - 1, 0)}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Paper>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              xl: "minmax(320px, 0.9fr) minmax(0, 1.6fr)",
            },
            alignItems: "start",
          }}
        >
          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Daily milk pricing
            </Typography>
            {canManageMilk ? (
              <Box component="form" onSubmit={onPriceSubmit}>
                <Stack spacing={1.5}>
                  {editingPriceId ? (
                    <Alert severity="info" action={<Button color="inherit" size="small" onClick={cancelPriceEdit}>Cancel</Button>}>
                      Editing milk price entry
                    </Alert>
                  ) : null}
                  <TextField
                    label="Effective date"
                    type="date"
                    value={priceForm.effective_date}
                    onChange={(e) => setPriceForm((prev) => ({ ...prev, effective_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    required
                  />
                  <TextField
                    label="Price per liter"
                    type="number"
                    value={priceForm.price_per_liter}
                    onChange={(e) => setPriceForm((prev) => ({ ...prev, price_per_liter: e.target.value }))}
                    size="small"
                    required
                  />
                  <TextField
                    label="Notes"
                    value={priceForm.notes}
                    onChange={(e) => setPriceForm((prev) => ({ ...prev, notes: e.target.value }))}
                    size="small"
                  />
                  <Stack direction="row" spacing={1}>
                    <Button type="submit" variant="contained">
                      {editingPriceId ? "Update milk price" : "Save milk price"}
                    </Button>
                    {editingPriceId ? (
                      <Button type="button" variant="outlined" onClick={cancelPriceEdit}>
                        Cancel
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                You can view pricing history, but only milk managers can change it.
              </Alert>
            )}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button size="small" variant="outlined" onClick={exportMilkPricesCsv}>
                Export CSV
              </Button>
            </Box>
            {isMobile ? (
              <Stack spacing={1.5}>
                {prices.length === 0 ? (
                  <Typography color="text.secondary">No milk prices recorded.</Typography>
                ) : (
                  prices.map((row) => (
                    <MobileListCard
                      key={row.id}
                      title={row.effective_date || "—"}
                      subtitle={formatCurrency(row.price_per_liter)}
                      fields={[{ label: "Notes", value: row.notes || "—" }]}
                      actions={
                        canManageMilk ? (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => startPriceEdit(row)}
                          >
                            Edit
                          </Button>
                        ) : null
                      }
                    />
                  ))
                )}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, pt: 1 }}>
                  <Button
                    size="small"
                    disabled={pricePage === 0}
                    onClick={() => setPricePage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: "center" }}>
                    Page {pricePage + 1} of{" "}
                    {Math.max(1, Math.ceil(priceTotal / pricePageSize))}
                  </Typography>
                  <Button
                    size="small"
                    disabled={(pricePage + 1) * pricePageSize >= priceTotal}
                    onClick={() => setPricePage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Stack>
            ) : (
            <DataGrid
              rows={prices}
              columns={priceColumns}
              getRowId={(r) => r.id}
              autoHeight
              rowCount={priceTotal}
              paginationMode="server"
              paginationModel={{ page: pricePage, pageSize: pricePageSize }}
              onPaginationModelChange={(model) => {
                setPricePage(model.page);
                setPricePageSize(model.pageSize);
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              disableRowSelectionOnClick
              sx={{ border: "none" }}
            />
            )}
          </Paper>

          <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider" }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Milk sales
            </Typography>
            {canManageMilk ? (
              <Box component="form" onSubmit={onSaleSubmit}>
                <Grid container spacing={1.5}>
                  {editingSaleId ? (
                    <Grid item xs={12}>
                      <Alert severity="info" action={<Button color="inherit" size="small" onClick={cancelSaleEdit}>Cancel</Button>}>
                        Editing milk sale entry
                      </Alert>
                    </Grid>
                  ) : null}
                  <Grid item xs={12} sm={6} lg={3}>
                    <TextField
                      fullWidth
                      label="Sale date"
                      type="date"
                      value={saleForm.sale_date}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, sale_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={3}>
                    <TextField
                      fullWidth
                      label="Buyer"
                      value={saleForm.buyer}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, buyer: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={2}>
                    <TextField
                      fullWidth
                      label="Liters sold"
                      type="number"
                      value={saleForm.liters_sold}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, liters_sold: e.target.value }))}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={2}>
                    <TextField
                      fullWidth
                      label="Unit price"
                      type="number"
                      value={saleForm.unit_price}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, unit_price: e.target.value }))}
                      size="small"
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={2}>
                    <TextField
                      fullWidth
                      label="Payment status"
                      select
                      value={saleForm.payment_status}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, payment_status: e.target.value }))}
                      size="small"
                    >
                      <MenuItem value="Paid">Paid</MenuItem>
                      <MenuItem value="Partially Paid">Partially Paid</MenuItem>
                      <MenuItem value="Unpaid">Unpaid</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <TextField
                      fullWidth
                      label="Payment method"
                      value={saleForm.payment_method}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, payment_method: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} lg={5}>
                    <TextField
                      fullWidth
                      label="Notes"
                      value={saleForm.notes}
                      onChange={(e) => setSaleForm((prev) => ({ ...prev, notes: e.target.value }))}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} lg={3}>
                    <Button type="submit" variant="contained" fullWidth sx={{ minHeight: 40 }}>
                      {editingSaleId ? "Update milk sale" : "Log milk sale"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ) : null}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => exportMilkSalesCsv(buildRangeParams(range, customStartDate, customEndDate))}
              >
                Export CSV
              </Button>
            </Box>
            {isMobile ? (
              <Stack spacing={1.5}>
                {sales.length === 0 ? (
                  <Typography color="text.secondary">No milk sales recorded.</Typography>
                ) : (
                  sales.map((row) => (
                    <MobileListCard
                      key={row.id}
                      title={row.sale_date || "—"}
                      subtitle={row.buyer || "—"}
                      status={
                        <Chip label={row.payment_status || "—"} size="small" />
                      }
                      fields={[
                        {
                          label: "Liters",
                          value: `${formatNumber(row.liters_sold, 2)} L`,
                        },
                        {
                          label: "Amount",
                          value: formatCurrency(row.total_amount),
                        },
                      ]}
                      actions={
                        canManageMilk ? (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => startSaleEdit(row)}
                          >
                            Edit
                          </Button>
                        ) : null
                      }
                    />
                  ))
                )}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, pt: 1 }}>
                  <Button
                    size="small"
                    disabled={salesPage === 0}
                    onClick={() => setSalesPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" sx={{ alignSelf: "center" }}>
                    Page {salesPage + 1} of{" "}
                    {Math.max(1, Math.ceil(salesTotal / salesPageSize))}
                  </Typography>
                  <Button
                    size="small"
                    disabled={(salesPage + 1) * salesPageSize >= salesTotal}
                    onClick={() => setSalesPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Box>
              </Stack>
            ) : (
            <DataGrid
              rows={sales}
              columns={saleColumns}
              getRowId={(r) => r.id}
              autoHeight
              rowCount={salesTotal}
              paginationMode="server"
              paginationModel={{ page: salesPage, pageSize: salesPageSize }}
              onPaginationModelChange={(model) => {
                setSalesPage(model.page);
                setSalesPageSize(model.pageSize);
              }}
              pageSizeOptions={[5, 10, 25, 50]}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 300 },
                  printOptions: { disableToolbarButton: true },
                },
              }}
              disableRowSelectionOnClick
              sx={{ border: "none" }}
            />
            )}
          </Paper>
        </Box>
      </Paper>
      <Dialog open={quickCaptureOpen} onClose={closeQuickCapture} fullWidth maxWidth="sm">
        <DialogTitle>Quick milk capture</DialogTitle>
        <Box component="form" onSubmit={onQuickCaptureSubmit}>
          <DialogContent dividers>
            <Stack spacing={2}>
              {quickCaptureError ? <Alert severity="error">{quickCaptureError}</Alert> : null}
              {quickCaptureSuccess ? <Alert severity="success">{quickCaptureSuccess}</Alert> : null}
              <Autocomplete
                options={quickAnimalOptions}
                loading={quickCaptureLoading}
                value={quickAnimal}
                onChange={(_, value) => {
                  setQuickAnimal(value);
                  setQuickCaptureError("");
                }}
                inputValue={quickAnimalInput}
                onInputChange={(_, value) => setQuickAnimalInput(value)}
                getOptionLabel={(option) =>
                  `${option?.tag_number || "No tag"}${option?.alias ? ` - ${option.alias}` : ""}`
                }
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                noOptionsText={
                  quickAnimalInput.trim().length < 2
                    ? "Type at least 2 characters"
                    : "No eligible cows found"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search cow"
                    placeholder="Tag number or alias"
                    helperText="Only milking-eligible animals are shown."
                  />
                )}
              />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    value={quickDate}
                    onChange={(e) => setQuickDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Morning (L)"
                    type="number"
                    value={quickMorning}
                    onChange={(e) => setQuickMorning(e.target.value)}
                    inputProps={{ min: 0, step: "0.1" }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Evening (L)"
                    type="number"
                    value={quickEvening}
                    onChange={(e) => setQuickEvening(e.target.value)}
                    inputProps={{ min: 0, step: "0.1" }}
                    required
                  />
                </Grid>
              </Grid>
              <TextField
                label="Total (L)"
                value={formatNumber(Number(quickMorning || 0) + Number(quickEvening || 0), 2)}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeQuickCapture} disabled={quickCaptureSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={quickCaptureSaving}>
              Save milking record
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
}
